import { useEffect } from 'react'

import { yupResolver } from '@hookform/resolvers/yup'
import { Volume2, Info } from 'lucide-react'
import { useForm, Controller } from 'react-hook-form'
import * as Yup from 'yup'

import { UpdateZoneRequestDTO } from '@/core/api/dtos/request/zone/update'
import { Zone } from '@/core/api/entities/zone/zone'
import { useTranslation } from '@/core/i18n/useTranslation'
import { Button } from '@/shared/components/atoms/Button'
import { Label } from '@/shared/components/atoms/Label/Label'
import { Slider } from '@/shared/components/atoms/Slider'
import {
	Dialog,
	DialogContent,
	DialogTitle,
} from '@/shared/components/molecules/Dialog/dialog'

interface ChangeVolumeModalProps {
	visible: boolean
	zone: Zone | null
	onClose: () => void
	onConfirm: (data: UpdateZoneRequestDTO) => void
	isUpdating: boolean
}

interface VolumeFormData {
	volume: number
}

export const ChangeVolumeModal = ({
	visible,
	zone,
	onClose,
	onConfirm,
	isUpdating,
}: ChangeVolumeModalProps) => {
	const { t } = useTranslation('zones')

	// Helper function to calculate the volume value
	const getVolumeValue = (z: Zone | null): number => {
		if (!z) return 3

		// If boxVolume exists, clamp it to 0-7 range
		if (z.boxVolume !== null && z.boxVolume !== undefined) {
			return Math.min(Math.max(z.boxVolume, 0), 7)
		}

		// If boxVolume doesn't exist, use zone.volume / 7 (as integer)
		if (z.volume !== null && z.volume !== undefined) {
			return Math.min(Math.max(Math.floor(z.volume / 7), 0), 7)
		}

		// Default fallback
		return 3
	}

	const volumeSchema = Yup.object().shape({
		volume: Yup.number()
			.required(t('modals.changeVolume.validation.required'))
			.min(0, t('modals.changeVolume.validation.min'))
			.max(7, t('modals.changeVolume.validation.max'))
			.integer(t('modals.changeVolume.validation.integer')),
	})

	const {
		control,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<VolumeFormData>({
		resolver: yupResolver(volumeSchema),
		defaultValues: {
			volume: getVolumeValue(zone),
		},
	})

	// Reset form when zone changes
	useEffect(() => {
		if (zone) {
			reset({
				volume: getVolumeValue(zone),
			})
		}
	}, [zone, reset])

	const onSubmit = (data: VolumeFormData) => {
		if (!zone) return

		const updateData: UpdateZoneRequestDTO = {
			name: zone.name,
			box_volume: data.volume,
		}

		onConfirm(updateData)
	}

	const handleClose = () => {
		reset()
		onClose()
	}

	const hasActiveBoxAllocation = zone?.getActiveBoxAllocation() !== null
	const isSmartVolumeEnabled = zone?.isSmartVolumeEnabled ?? false

	return (
		<Dialog open={visible} onOpenChange={handleClose}>
			<DialogContent
				className="sm:max-w-[500px] max-h-[90vh] p-0 gap-0 overflow-hidden flex flex-col"
				showCloseButton={false}
			>
				{/* Header */}
				<div className="px-6 py-5 border-b">
					<DialogTitle className="text-lg">
						{t('modals.changeVolume.title')}
					</DialogTitle>
				</div>

				{/* Content */}
				<form
					onSubmit={handleSubmit(onSubmit)}
					className="flex flex-col flex-1 min-h-0"
				>
					<div className="px-6 py-6 space-y-6 overflow-y-auto flex-1">
						{/* Smart Volume Warning */}
						{isSmartVolumeEnabled && hasActiveBoxAllocation && (
							<div className="text-sm text-yellow-500 bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-3">
								{t('modals.changeVolume.smartVolumeWarning')}
							</div>
						)}

						{/* Pending Volume Change Notice */}
						{zone?.hasVolumeChangePending && (
							<div className="text-sm text-blue-500 bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
								{t('modals.changeVolume.pendingChangeNotice')}
							</div>
						)}

						{/* Volume Slider */}
						<div className="space-y-2">
							<div className="flex items-center gap-2">
								<Label className="text-sm font-medium text-muted-foreground">
									{t('modals.changeVolume.volumeLabel')}
								</Label>
								<Info className="h-4 w-4 text-muted-foreground" />
							</div>

							<Controller
								name="volume"
								control={control}
								render={({ field }) => (
									<div className="flex border rounded-lg p-3 items-center justify-between gap-3 bg-background">
										<Volume2 className="h-5 w-5 text-muted-foreground flex-shrink-0" />
										<Slider
											value={[field.value]}
											onValueChange={(value) => field.onChange(value[0])}
											max={7}
											min={0}
											step={1}
											className="flex-1"
											disabled={isUpdating}
										/>
										<span className="text-sm font-semibold w-8 text-right flex-shrink-0">
											{field.value}
										</span>
									</div>
								)}
							/>

							{errors.volume && (
								<p className="text-sm text-red-500">{errors.volume.message}</p>
							)}
						</div>
					</div>

					{/* Footer */}
					<div className="px-6 py-5 border-t flex justify-end gap-3">
						<Button
							type="button"
							variant="outline"
							onClick={handleClose}
							disabled={isUpdating}
							className="rounded-full px-6"
						>
							{t('modals.changeVolume.cancel')}
						</Button>
						<Button
							type="submit"
							variant="default"
							disabled={isUpdating}
							className="rounded-full px-6"
						>
							{isUpdating
								? t('modals.changeVolume.updating')
								: t('modals.changeVolume.confirm')}
						</Button>
					</div>
				</form>
			</DialogContent>
		</Dialog>
	)
}
